<template>
	<component
	ref="rootEl"
	:class="classes"
	:is="nodeType"
	:type="props.type=='checkbox'? null : props.type"
	:target="props.target"
	:to="props.to"
	:href="props.type=='link'? props.to : null"
	@click.stop="onClick($event)"
	:style="progressStyle">
		<img :src="parsedIcon" v-if="parsedIcon && !isIconSVG" alt="icon" class="icon" :class="props.loading? 'hide' : 'show'">
		<div v-html="parsedIcon" v-if="parsedIcon && isIconSVG" alt="icon" class="icon" :class="props.loading? 'hide' : 'show'"></div>

		<div class="checkboxContent" v-if="props.type=='checkbox'">
			<div class="checkmark">
				<img :src="checkMarkIcon" v-if="checked" alt="icon" class="img">
			</div>
			<span class="label" :class="props.loading? 'hide' : 'show'" v-if="props.title" v-html="props.title"></span>
			<input type="checkbox" :name="props.name" :id="props.name" class="checkboxInput" v-model="checked" v-if="props.type=='checkbox'" />
		</div>

		<img src="@/assets/loader/loader.svg" alt="loader" class="spinner" v-if="props.loading">
		<span class="label" :class="props.loading? 'hide' : 'show'" v-if="props.title && props.type!='checkbox'" v-html="props.title"></span>
		<input type="file" v-if="props.type=='file'" class="browse" :accept="props.accept" ref="browse" @change="onBrowseFile()" />
	</component>
</template>

<script setup lang="ts">
import gsap from 'gsap';
import { computed, ref, useTemplateRef, watch, type ComponentPublicInstance, type StyleValue } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

const props = withDefaults(defineProps<{
	icon?:string;
	iconSelected?:string;
	title?:string;
	name?:string;
	file?:string;
	loading?:boolean;
	type?:string;
	target?:string;
	to?:RouteLocationRaw;
	percent?:number;
	white?:boolean;
	big?:boolean;
	small?:boolean;
	highlight?:boolean;
	selected?:boolean;
	disabled?:boolean;
	modelValue?:boolean;
	bounce?:boolean;
	accept?:string;
}>(), {
	loading: false,
	type: 'button',
	percent: -1,
	white: false,
	big: false,
	small: false,
	highlight: false,
	selected: false,
	disabled: false,
	modelValue: false,
	bounce: false,
	accept: "image/*",
});

const emit = defineEmits<{
	click: [event:MouseEvent];
	'update:modelValue': [value:boolean];
	'update:file': [file:File];
}>();

const rootEl = useTemplateRef<HTMLElement | ComponentPublicInstance>('rootEl');
const browse = useTemplateRef<HTMLInputElement>('browse');

const pInterpolated = ref<number>(-1);
const checked = ref<boolean>(props.modelValue);

const checkMarkIcon = new URL('../assets/icons/checkmark.svg', import.meta.url).href;

const nodeType = computed<string>(() => {
	if(props.to) return "router-link";
	if(props.type == "checkbox") return "div";
	if(props.type == "link") return "a";
	return "button";
});

const parsedIcon = computed<string | undefined>(() => {
	if(props.selected !== false && props.iconSelected) {
		return props.iconSelected;
	}else{
		return props.icon;
	}
});

const isIconSVG = computed<boolean>(() => (parsedIcon.value ?? "").indexOf("<") != -1);

const progressStyle = computed<StyleValue>(() => {
	if(pInterpolated.value > -1 && pInterpolated.value < 100) {
		const p:number = Math.round(pInterpolated.value);
		let color = "255, 255, 255";
		let alpha = .5;
		if(props.white !== false) {
			color = "75, 201, 194"
			alpha = .3;
		}
		return {backgroundImage: "linear-gradient(to right, rgba("+color+",0) "+p+"%,rgba("+color+",0) "+p+"%,rgba("+color+","+alpha+") "+p+"%,rgba("+color+","+alpha+") 100%)"};
	}else{
		return {};
	}
});

const classes = computed<string[]>(() => {
	const list =  ["button"]
	if(!props.title) list.push("noTitle");
	if(props.white !== false) list.push("white");
	if(props.big !== false) list.push("big");
	if(props.small !== false) list.push("small");
	if(props.highlight !== false) list.push("highlight");
	if(props.selected !== false) list.push("selected");
	if(checked.value !== false) list.push("checked");
	if(props.loading !== false) list.push("disabled", "loading");
	if(props.disabled !== false) list.push("disabled");
	if(props.type == "checkbox") list.push("checkbox");
	return list;
});

watch(checked, (val:boolean) => {
	emit("update:modelValue", val);
});

watch(() => props.modelValue, (val:boolean) => {
	checked.value = val;
});

watch(() => props.percent, (val:number) => {
	const duration = val < pInterpolated.value? 0 : .35;
	gsap.killTweensOf(pInterpolated);
	gsap.to(pInterpolated, {duration, value:val, ease:"sine.inout"});
});

/**
 * Root DOM node. When rendered as a router-link the template ref
 * is the RouterLink component instance, not the element itself.
 */
function getRootElement():HTMLElement {
	const el = rootEl.value!;
	return "$el" in el? el.$el as HTMLElement : el;
}

function onBrowseFile():void {
	const input = browse.value!;
	if(input.files) {
		emit('update:file', input.files[0])
	}
}

function resetBrowse():void {
	browse.value!.value = "";
}

function onClick(event:MouseEvent):void {
	if(props.disabled !== false || props.loading) {
		event.preventDefault();
		event.stopPropagation();
		return;
	}
	if(props.bounce) {
		const el = getRootElement();
		gsap.fromTo(el, {scaleX:.7}, {duration:1.4, scale:1, clearProps:"scaleX", ease:"elastic.out(2)"});
		gsap.fromTo(el, {scaleY:.7}, {duration:1.2, scale:1, clearProps:"all", ease:"elastic.out(2)", delay:.05});
	}
	emit("click", event);
}

defineExpose({ resetBrowse });
</script>

<style lang="less" scoped>

.button {
	position: relative;//Necessary for loader spinning absolute placement
	display: inline-flex;
	justify-content: center;
	align-items: center;
	flex-direction: row;
	white-space: nowrap;
	// transition: all .25s;
	overflow: hidden;
	// touch-action: none;
	user-select: none;
	cursor: pointer;
	border: none;
	padding: 5px 10px;
	font-size: 1em;
	color: @mainColor_dark;
	background-color: darken(@mainColor_normal, 10%);
	transition: color .25s, background-color .25s;
	box-sizing: border-box;
	text-align: center;
	border-radius: @border_radius;
	will-change: transform;
	text-decoration: none;
	min-height: calc(1em + 10px);

	&:hover {
		background-color: @mainColor_normal;
	}

	&.dark {
		color: #fff;
		background-color: @mainColor_dark;

		&:hover {
			background-color: @mainColor_dark_light;
		}
	}

	&.white {
		color: @mainColor_dark;
		background-color: @mainColor_light;

		&:hover {
			background-color: lighten(@mainColor_normal_extralight, 12%) !important;
		}
	}

	&.disabled {
		cursor: not-allowed;
		color: fade(@mainColor_dark, 25%);
		&.dark {
			color: fade(#000, 25%);
			background-color: fade(@mainColor_dark_extralight, 50%);
		}
	}

	&.loading {
		cursor: wait;
	}

	&>*:not(.browse) {
		pointer-events: none;
	}

	&.noTitle {
		margin: 0;
		padding: 7px;
		.icon {
			height: 100%;
			max-height: 1em;
			min-width: 1em;
			margin: 0;
			padding: 0;
		}

		&.big {
			padding: 19px;
			font-size: 1.25em;
		}

	}

	&.checkbox {
		background: none;
		padding: 0px;
		border-radius: 0;
		margin: 0;
		display: inline-block;

		.checkboxInput {
			pointer-events: all;
			opacity: .001;
			position: absolute;
			padding: 0;
			margin: 0;
			width: 100%;
			height: 100%;
			left: 0;
			top: 0;
			z-index: 1000;
			cursor: pointer;
		}
	}

	.checkboxContent {
		cursor: pointer;
		display: flex;
		flex-direction: row;
		width: 100%;
		height: 100%;
		align-items: center;

		.checkmark {
			border: 1px solid @mainColor_normal;
			border-radius: 4px;
			padding: 0;
			width: 15px;
			height: 15px;
			box-sizing: border-box;
			display: flex;
			align-items: center;
			justify-content: center;
			.img {
				width: 80%;
				margin: 0;
				padding: 0;
			}
		}

		.label {
			flex-grow: 1;
			margin-left: 7px;
			justify-self: flex-start;
			text-align: left;
			width: max-content;
			color: @mainColor_normal;
			// overflow: visible;
		}
		
		&:hover {
			background: none;
			.checkmark {
				background-color: fade(@mainColor_normal; 30%);
			}
		}
	}

	.icon {
		max-height: 1em;
		height: 1em;
		margin-right: 10px;
		vertical-align: text-top;
	}

	.spinner {
		.center;
		position: absolute;
		vertical-align: middle;
		height: 25px;
		width: 25px;
	}

	.label {
		// flex-grow: 1;
		white-space: nowrap;
		// overflow: hidden;
	}

	.label, .icon {
		opacity: 1;
		transition: opacity .2s;
		&.hide {
			opacity: .25;
		}
	}

	.browse {
		opacity: 0;
		position: absolute;
		z-index: 0;
		left: 0;
		width: 100%;
		height: 200%;//Hack to avoid browse button from locking cursor:pointer by putting it out of button's bounds
		cursor: pointer;
		font-size: 0px;
	}

	&.white {
		.label, .icon {
			&.hide {
				opacity: .4;
			}
		}
		&:not(.loading):hover {
			background-color: @mainColor_normal_extralight;
		}
		&.loading {
			background-color: fade(#ffffff, 50%);
		}
		.checkboxContent {
			.checkmark {
				border-color: #fff;
			}
			.label {
				color: #fff;
			}
		}
	}

	&.big {
		padding: .5em 1em;
		font-size: 1.25em;
		min-height: calc(1.25em + 40px);
		&.checkbox {
			padding: 0;
			.checkboxContent {
				.checkmark {
					border-radius: 13px;
					width: 40px;
					height: 40px;
				}
			}
		}
		.icon {
			max-height: 1.5em;
			height: 1.5em;
		}
	}

	&.small {
		padding: 4px;
		border-radius: 5px;
		font-size: .85em;
		min-height: calc(.85em + 8px);
		&.checkbox {
			padding: 0;
			.checkboxContent {
				.checkmark {
					width: 18px;
					height: 18px;
				}
			}
		}
	}

	&.highlight {
		color: #ffffff;
		background-color: @mainColor_alert;
		&.disabled {
			background-color: fade(@mainColor_alert,50%);
		}
		.label, .icon {
			&.hide {
				opacity: .4;
			}
		}
		&:not(.loading):hover {
			background-color: @mainColor_alert_light;
		}
		&.loading {
			background-color: fade(@mainColor_alert, 50%);
		}
		&.selected {
			background-color: @mainColor_alert_extralight;
		}
	}

	&.selected:not(.highlight) {
		background-color: @mainColor_warn;
		color: #fff;
		&.disabled {
			background-color: fade(@mainColor_warn,50%);
		}
		&:hover {
			background-color: @mainColor_warn_light;
		}
	}
	
	&.disabled {
		color: fade(@mainColor_normal, 45%);
		background-color: fade(@mainColor_normal, 30%);
		&:hover {
			background-color: fade(@mainColor_normal, 30%);
		}
		.icon {
			opacity: .4;
		}
	}
}

@media only screen and (max-width: 500px) {
	.button {
		&.noTitle.big, &.big {
			padding: 12px;
			font-size: 1.2em;
			min-height: calc(1.2em + 12px);
		}
		&:not(.big) {
			font-size: .85em;
			min-height: calc(.85em + 12px);
		}
	}
}
</style>